import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationSerializer from 'lumen/serializers/application';
import { v4 as uuidv4 } from 'uuid';

type RawInsightEntry = {
  id: string;
  info: {
    insight_element_type: string;
    stay_date: string;
    text: {
      template: string;
      template_key: string;
      variables: Record<string, string>;
    }[];
  };
  type: string;
};

type FormattedInsightEntry = {
  id: string;
  info: {
    insight_element_type: string;
    stay_date: string;
    text: {
      template: string;
      template_key: string;
      variables: Record<string, string>;
    };
  };
  type: string;
};

type RawRecommendationEntry = {
  config: Record<string, unknown>;
  id: string;
  info: {
    recommendation_element_type?: string;
    text: {
      template: string;
      template_key: string;
      variables: Record<string, string>;
    }[];
  };
  type: string;
};

type FormattedRecommendationEntry = {
  config: Record<string, unknown>;
  id: string;
  info: {
    recommendation_element_type?: string;
    text: {
      template: string;
      template_key: string;
      variables: Record<string, string>;
    };
  };
  type: string;
};

const GENERATION_STATUS = {
  GENERATING: 'generating',
  FAILED: 'failed',
} as const;

export type SmartInsightDayResponseMeta = {
  biRedirectId: string;
  featureFlags: string[];
  isFailed?: boolean;
  isGenerating?: boolean;
  rateiRedirectId: string;
  retryAfterSeconds?: number;
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default class SmartInsightDaySerializer extends ApplicationSerializer.extend(
  EmbeddedRecordsMixin,
) {
  attrs = {
    insightEntries: { embedded: 'always' },
    kpis: { embedded: 'always' },
    recommendationEntries: { embedded: 'always' },
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  extractMeta(store: Store, modelClass: ModelSchema, payload: object) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    let meta = super.extractMeta(store, modelClass, payload);
    if (!meta) {
      meta = {};
    }

    const camelizedMeta = {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      featureFlags: meta.feature_flags ?? [],
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      rateiRedirectId: meta.ratei_redirect_id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      biRedirectId: meta.bi_redirect_id,
    };

    return camelizedMeta;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  modelNameFromPayloadKey(key: string) {
    if (key === 'insights' || key === 'archived_insights') {
      return 'smart-insights/smart-insight-day';
    }

    return key;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  normalizeResponse(
    store: Store,
    primaryModelClass: ModelSchema,
    payload: {
      archived_insights?: {
        actioned?: boolean;
        actioned_status?: string | null;
        archived_on: string;
        data: {
          id: string;
          insight_entries: RawInsightEntry[];
          kpis: {
            adr: number;
            adr_pick_vs_yesterday: number;
            demand: number;
            lowest_rate?: number;
            median_compset_rate?: number;
            message?: string | null;
            occupancy: number;
            occupancy_pick_vs_yesterday: number;
            own_rate?: number;
          };
          recommendation_entries?: RawRecommendationEntry[];
          stay_date: string;
          stay_date_priority: string;
          stay_date_relevance: number;
        };
        insight_creation_time: string;
      }[];
      insights?: {
        id: string;
        insight_entries: RawInsightEntry[];
        kpis: null | {
          adr: number;
          adr_pickup_vs_yesterday: number;
          adr_stly: number;
          demand: number;
          lowest_rate?: number;
          median_compset_rate?: number;
          message?: string | null;
          occupancy: number;
          occupancy_pickup_vs_3_days_ago: number;
          occupancy_pickup_vs_7_days_ago: number;
          occupancy_pickup_vs_yesterday: number;
          occupancy_stly: number;
          occupancy_stly_pickup_vs_3_days_ago: number;
          occupancy_stly_pickup_vs_7_days_ago: number;
          occupancy_stly_pickup_vs_yesterday: number;
          own_rate?: number;
          room_nights: number;
          room_nights_pickup_vs_3_days_ago: number;
          room_nights_pickup_vs_7_days_ago: number;
          room_nights_pickup_vs_yesterday: number;
          room_nights_stly: number;
          room_nights_stly_pickup_vs_3_days_ago: number;
          room_nights_stly_pickup_vs_7_days_ago: number;
          room_nights_stly_pickup_vs_yesterday: number;
        };
        recommendation_entries?: RawRecommendationEntry[];
      }[];
      meta?: SmartInsightDayResponseMeta;
      retry_after_seconds?: number;
      status?: string;
    },
    id: string | number,
    requestType: string,
  ) {
    if (payload.status === GENERATION_STATUS.GENERATING) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
      const normalizedResponse = super.normalizeResponse(
        store,
        primaryModelClass,
        { insights: [] },
        id,
        requestType,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      normalizedResponse.meta = {
        isGenerating: true,
        retryAfterSeconds: payload.retry_after_seconds ?? 5,
      };
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return normalizedResponse;
    }

    if (payload.status === GENERATION_STATUS.FAILED) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
      const normalizedResponse = super.normalizeResponse(
        store,
        primaryModelClass,
        { insights: [] },
        id,
        requestType,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      normalizedResponse.meta = { isFailed: true };
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return normalizedResponse;
    }

    // Normalize the payload key: /archive returns `archived_insights` (wrapped), /insights returns `insights` (flat)
    const insightsArray =
      payload.insights ??
      payload.archived_insights?.map((item) => ({
        ...item.data,
        archived_at: item.archived_on,
        archive_reason:
          item.actioned_status ??
          (item.actioned ? 'action_taken' : 'not_actionable'),
        insight_creation_time: item.insight_creation_time,
      })) ??
      [];

    // API returns an insight_entries object with key value pairs, but we need to convert it to an array.
    // If the entry has multiple text objects, each text object should be treated as a separate entry.
    const payloadWithInsightEntries: {
      insights: {
        insight_entries: FormattedInsightEntry[];
        recommendation_entries: FormattedRecommendationEntry[];
      }[];
      meta?: SmartInsightDayResponseMeta;
    } = {
      insights: insightsArray.map((insight) => {
        // Generate a fresh UUID for the Ember Data record id to avoid collisions
        // between the active /insights and /archive endpoints, which return the same
        // composite backend id for the same stay date. The backend id is preserved as
        // `generation_id` so it can still be used for analytics / feedback tracking.
        const generationId = insight.id;
        const id = uuidv4();

        const insightEntries: FormattedInsightEntry[] = [];

        Object.values(insight.insight_entries)
          .filter((entry) => entry !== null && entry !== undefined)
          .forEach((entry) => {
            // The current tests are recorded with the old API format, where `text` is a string.
            // We've adjusted the API to return an array of text objects.
            // To avoid having to re-record all of the tests, I've added a check here to ensure compatibility with the old format.
            if (!Array.isArray(entry.info.text)) {
              return;
            }

            entry.info.text.forEach((text) => {
              insightEntries.push({
                ...entry,
                id: uuidv4(),
                type: 'smart-insights/smart-insight-day-insight-entry',
                info: {
                  ...entry.info,
                  text,
                },
              });
            });
          });

        const recommendationEntries: FormattedRecommendationEntry[] = [];

        Object.entries(insight.recommendation_entries ?? {})
          .filter(([, entry]) => entry !== null && entry !== undefined)
          .forEach(([key, entry]) => {
            if (!Array.isArray(entry.info.text)) {
              return;
            }

            entry.info.text.forEach((text) => {
              recommendationEntries.push({
                ...entry,
                id: uuidv4(),
                type: 'smart-insights/smart-insight-day-recommendation-entry',
                info: {
                  ...entry.info,
                  recommendation_element_type: key,
                  text,
                },
              });
            });
          });

        return {
          ...insight,
          id,
          generation_id: generationId,
          insight_entries: insightEntries,
          recommendation_entries: recommendationEntries,
          kpis: {
            ...Object.fromEntries(
              Object.entries(insight.kpis ?? {}).map(([key, value]) => [
                key.replace(/_vs_(\d+)_/g, '_vs$1_'),
                value,
              ]),
            ),
            id: uuidv4(),
          },
        };
      }),
      meta: payload.meta,
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
    const normalizedResponse = super.normalizeResponse(
      store,
      primaryModelClass,
      payloadWithInsightEntries,
      id,
      requestType,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return normalizedResponse;
  }
}
